import React from 'react';
import type { IconType } from 'react-icons';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: IconType;
  containerClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, containerClassName, icon: Icon, ...props }, ref) => {
    const isIOS = React.useMemo(() => {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      return (
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      );
    }, []);

    const showOverlayPlaceholder =
      isIOS && !props.disabled && !props.value && (props.type === 'time' || props.type === 'date');

    return (
      <div className={cn('relative flex items-center group min-w-0', containerClassName)}>
        {Icon && (
          <div className="absolute left-3 text-zinc-500 group-focus-within:text-white transition-colors">
            <Icon size={18} />
          </div>
        )}
        {showOverlayPlaceholder && (
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-y-0 flex items-center text-zinc-500 text-[14px]',
              Icon ? 'left-10' : 'left-4',
            )}
          >
            {props.placeholder}
          </span>
        )}
        <input
          ref={ref}
          className={cn(
            'w-full bg-zinc-900/50 border border-zinc-700 text-white rounded-lg py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 placeholder:text-zinc-600',
            Icon ? 'pl-10 pr-4' : 'px-4',
            showOverlayPlaceholder ? 'text-transparent caret-transparent' : '',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);

Input.displayName = 'Input';
